import { useCallback, useEffect, useRef } from 'react';
import type { PageState, ServerMessage } from '@roll1/shared';
import type { Camera, TokenSnapshot } from '../../types';
import { useSessionContext } from '../../contexts/SessionContext';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { useTokenContext } from '../../contexts/TokenContext';
import { useUndoContext } from '../../contexts/UndoContext';
import { useCanvasInteraction } from '../../contexts/CanvasInteractionContext';
import { useToolContext } from '../../contexts/ToolContext';
import type { ToolName } from '../../types';
import { useShortcutContext } from '../../contexts/ShortcutContext';
import { usePageLoader } from '../../hooks/usePageHydration';
import { deleteSelectedCanvasItems } from '../../hooks/useAppShortcuts';
import { applyEchoToCanvas, applyEchoToStoredPage } from '../../lib/canvas-page-echo';
import { useStudioMapPanes } from './StudioMapPanes';

/**
 * The page lifecycle of ONE GM Studio map pane (package 5.3). Runs inside the
 * pane's own providers (StudioMapCanvas): `pages` is the PageViewScope view,
 * the canvas/token/undo contexts are the pane's.
 *
 *  - mount: load the page from the store into the pane's hooks, then hold it
 *    (the Studio router sends its echoes here) and restore the pane's pan/zoom
 *    once the map has loaded (loading fits the camera);
 *  - a new join snapshot (reconnect): reload from the fresh store, never save
 *    the older hooks over it; echoes that arrive before the reload go to the
 *    fresh store, which the reload then brings in;
 *  - unmount: hand the live content back to the store — only if it was loaded
 *    from the current snapshot — remember pan/zoom, release the page.
 * Mount loads and unmount saves both run in passive effects, so when a page
 * moves from one pane to the other in one commit, the old pane saves first.
 */
export function useStudioMapPaneContent(pageId: string): ReturnType<typeof usePageLoader> {
  const { pages } = useSessionContext();
  const canvas = useCanvasContext();
  const tokenCtx = useTokenContext();
  const undoStack = useUndoContext();
  const panes = useStudioMapPanes();
  const loader = usePageLoader();

  const latest = useRef({ pages, canvas, tokenCtx, undoStack, loader });
  latest.current = { pages, canvas, tokenCtx, undoStack, loader };

  // The snapshot (hydrationSeqRef value) the hooks were loaded from; null = not yet.
  const loadedSeqRef = useRef<number | null>(null);
  const loadTokenRef = useRef(0);
  // The load whose tokens, notes and pins have rendered (they are React state: a load is in them only after its
  // commit). Declared before the load effect, so the commit of the mount itself does not count as the load's.
  const renderedLoadRef = useRef(0);
  useEffect(() => {
    renderedLoadRef.current = loadTokenRef.current;
  }, [tokenCtx.tokens.tokens, tokenCtx.notes.notes, tokenCtx.pins.pins]);
  /** Save to the store; the lists are kept from the store until the latest load has rendered. */
  const saveToStore = useCallback((): void => {
    latest.current.loader.saveCurrentPageToStore({ keepLists: renderedLoadRef.current !== loadTokenRef.current });
  }, []);

  const isCurrent = useCallback(
    (): boolean => loadedSeqRef.current === latest.current.pages.hydrationSeqRef.current,
    [],
  );

  const load = useCallback((page: PageState, camera: Camera | undefined): void => {
    const token = ++loadTokenRef.current;
    loadedSeqRef.current = latest.current.pages.hydrationSeqRef.current;
    void Promise.resolve(latest.current.loader.loadPageToHooks(page)).then(() => {
      if (token !== loadTokenRef.current || camera === undefined) return;
      const { engine } = latest.current.canvas;
      engine.setCamera(camera);
      engine.markAllDirty();
    });
  }, []);

  useEffect(() => {
    const pd = pages.getPageData(pageId);
    if (pd === null) return;
    const first = loadedSeqRef.current === null;
    if (!first) {
      // Reloaded content: undo entries of the older hooks no longer apply
      // (the table clears its stack on reconnect too).
      latest.current.undoStack.clear();
    }
    load(pd, first ? panes.recallCamera(pageId) : latest.current.canvas.engine.cameraRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, pages.hydrationSeq]);

  useEffect(() => {
    const release = panes.register({
      pageId,
      apply: (msg: ServerMessage, page: PageState | null): void => {
        if (!isCurrent()) {
          // A snapshot replaced the store and the reload is pending: the echo
          // belongs to the fresh copy the pane is about to load.
          if (page) applyEchoToStoredPage(page, msg);
          return;
        }
        const { canvas: c, tokenCtx: tc } = latest.current;
        applyEchoToCanvas(
          {
            tokens: tc.tokens, notes: tc.notes, pins: tc.pins,
            engine: c.engine, drawing: c.drawing, fog: c.fog, dynamicFog: c.dynamicFog,
            wallsHook: c.wallsHook, mapObjects: c.mapObjects, locationLinks: c.locationLinks,
            globalIlluminationRef: c.globalIlluminationRef,
          },
          msg,
          page,
        );
      },
      saveToStore: (): void => {
        if (isCurrent()) saveToStore();
      },
      reload: (page: PageState): void => {
        load(page, latest.current.canvas.engine.cameraRef.current);
      },
    });
    return () => {
      if (isCurrent()) saveToStore();
      panes.rememberCamera(pageId, latest.current.canvas.engine.cameraRef.current);
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  return loader;
}

/**
 * Canvas shortcuts of a GM Studio map pane: undo / redo of this pane's stack,
 * Delete of its selection and the letter keys of the tools (S V B E R F N L T A O — as at the table). Registered in the session's ShortcutContext,
 * which runs ONE enabled entry per key press — and only the working map's
 * entries are enabled. A focused input never triggers them (ShortcutContext's
 * input guard).
 */
export function useStudioMapShortcuts(pageId: string, ownsKeyboard: () => boolean): void {
  const registry = useShortcutContext();
  const { mp, isGM } = useSessionContext();
  const { tokens } = useTokenContext();
  const undoStack = useUndoContext();
  const { get } = useCanvasInteraction();
  const { setTool } = useToolContext();

  const d = useRef({ mp, isGM, tokens, undoStack, get, ownsKeyboard, setTool });
  d.current = { mp, isGM, tokens, undoStack, get, ownsKeyboard, setTool };

  useEffect(() => {
    const when = `studio-map:${pageId}`;
    const enabled = (): boolean => d.current.ownsKeyboard();
    const sendCreateRef = {
      current: (token: TokenSnapshot): void => {
        if (d.current.mp.session) d.current.mp.send({ type: 'token:create', token, pageId });
      },
    };
    const sendDeleteRef = {
      current: (tokenId: string): void => {
        if (d.current.mp.session) d.current.mp.send({ type: 'token:delete', tokenId, pageId });
      },
    };
    const deleteSelection = (): void => {
      const { mp: m, isGM: gm, tokens: tk, undoStack: undo, get: g } = d.current;
      deleteSelectedCanvasItems({
        textHook: {
          selectedTextId: g('text:selected-id')?.() ?? null,
          deleteText: (id: string) => g('text:delete-selected')?.(id),
          setSelectedTextId: () => {},
        },
        handleDeleteText: (id: string) => g('text:delete-selected')?.(id),
        tokens: tk,
        mp: m,
        outgoingPageId: pageId,
        undoStack: undo,
        sendCreateRef,
        sendDeleteRef,
        isGM: gm,
      });
    };
    // The letter keys of the tools act on the working map's own tool state (the pane's ToolProvider).
    // The table's own registrations (useAppShortcuts) are not mounted in Studio, so nothing doubles.
    const toolKeys: ReadonlyArray<{ keys: string; tool: ToolName; label: string; gmOnly: boolean }> = [
      { keys: 'S', tool: 'select', label: 'shortcuts.toolSelect', gmOnly: false },
      { keys: 'V', tool: 'select-drawing', label: 'shortcuts.toolSelectDrawing', gmOnly: true },
      { keys: 'B', tool: 'draw', label: 'shortcuts.toolDraw', gmOnly: false },
      { keys: 'E', tool: 'erase', label: 'shortcuts.toolEraser', gmOnly: false },
      { keys: 'R', tool: 'measure', label: 'shortcuts.toolRuler', gmOnly: false },
      { keys: 'P', tool: 'ping', label: 'shortcuts.toolPing', gmOnly: false },
      { keys: 'F', tool: 'fog', label: 'shortcuts.toolFog', gmOnly: false },
      { keys: 'N', tool: 'note', label: 'shortcuts.toolNote', gmOnly: true },
      { keys: 'L', tool: 'location-link', label: 'shortcuts.toolPortal', gmOnly: true },
      { keys: 'T', tool: 'text', label: 'shortcuts.toolText', gmOnly: true },
      { keys: 'A', tool: 'aoe', label: 'shortcuts.toolAoe', gmOnly: true },
      { keys: 'O', tool: 'map-object', label: 'shortcuts.toolMapObject', gmOnly: true },
    ];
    const toolRegistrations = toolKeys.map(({ keys, tool, label, gmOnly }) =>
      registry.register({
        id: `${when}:tool:${tool}`,
        keys,
        label: label as never,
        category: 'tool',
        when,
        enabled: () => enabled() && (!gmOnly || d.current.isGM),
        action: () => d.current.setTool(tool),
      }),
    );
    const unregister = [
      ...toolRegistrations,
      registry.register({ id: `${when}:undo`, keys: 'Ctrl+Z', label: 'shortcuts.undo', category: 'global', when, enabled, action: () => d.current.undoStack.undo() }),
      registry.register({ id: `${when}:redo`, keys: 'Ctrl+Shift+Z', label: 'shortcuts.redo', category: 'global', when, enabled, action: () => d.current.undoStack.redo() }),
      registry.register({ id: `${when}:delete`, keys: 'Delete', label: 'shortcuts.deleteTokens', category: 'canvas', when, enabled, action: deleteSelection }),
      registry.register({ id: `${when}:backspace`, keys: 'Backspace', label: 'shortcuts.deleteTokens', category: 'canvas', when, enabled, action: deleteSelection }),
    ];
    return () => { for (const u of unregister) u(); };
  }, [registry, pageId]);
}
